import _ from 'lodash'
import { ClientCampaignCard } from '/fiweb/components/CampaignCard'
import { CardTypeEnum } from '/fiweb/components/CampaignCard/types'
import dayjs from 'dayjs'

export const sortOngoingCampaignCards = (cards: Array<ClientCampaignCard>) => {
  if (_.isEmpty(cards)) {
    return []
  }

  const sortedCampaignCards = [...cards].sort((a, b) => {
    // Check if both have reservations last 24 hours
    const aCount = a.recentSubscriptionsCount
    const bCount = b.recentSubscriptionsCount

    if (aCount && bCount) {
      if (aCount > bCount) {
        // A has more reservations, put it first
        return -1
      }
      if (aCount < bCount) {
        // B has more reservations, put it first
        return 1
      }
      // Both have the same amount, put the one with nearest close date first
      return dayjs(b?.dates?.close).isAfter(a?.dates?.close) ? 1 : -1
    }
    if (aCount && !bCount) {
      // A has reservations, B has not. Put A first
      return -1
    }
    if (!aCount && bCount) {
      // B has reservations, A has not. Put B first
      return 1
    }
    // None has reservations last 24 hours, check if they have reservations at all
    const subCountA = a.subscriptionsCount
    const subCountB = b.subscriptionsCount
    if (subCountA && !subCountB) {
      // A has reservations, B don't, put A first
      return -1
    }
    if (!subCountA && subCountB) {
      // B has reservations, A don't, put B first
      return 1
    }
    if (subCountA && subCountB) {
      // Both has reservations, sort by amount of reservations
      return subCountB - subCountA
    }
    // None has reservations, sort by closeDate
    return dayjs(b?.dates?.close).isAfter(a?.dates?.close) ? 1 : -1
  })

  return sortedCampaignCards
}

export const sortUpcomingCampaignCards = (cards: Array<ClientCampaignCard>) => {
  if (_.isEmpty(cards)) {
    return []
  }

  return [...cards].sort((a, b) => {
    if (a.recentPageViewsCount === b.recentPageViewsCount) {
      const aStart = dayjs(a.dates?.open).valueOf()
      const bStart = dayjs(b.dates?.open).valueOf()
      if (aStart === bStart) {
        return b.followerCount - a.followerCount
      }
      return aStart - bStart
    }
    return b.recentPageViewsCount - a.recentPageViewsCount
  })
}

export const sortComingSoonCards = (cards: Array<ClientCampaignCard>) =>
  [...(cards || [])].sort((a, b) => b.followerCount - a.followerCount)

export const sortFinishedCampaignCards = (cards: Array<ClientCampaignCard>) => {
  if (_.isEmpty(cards)) {
    return []
  }

  const success = cards.filter((card) => card.finishedSuccessfully)
  const failed = cards.filter((card) => !card.finishedSuccessfully)
  return [
    ...success.sort((a, b) => (dayjs(a.dates?.close).isAfter(dayjs(b.dates?.close)) ? -1 : 1)),
    ...failed.sort((a, b) => (dayjs(a.dates?.close).isAfter(dayjs(b.dates?.close)) ? -1 : 1)),
  ]
}

export const sortCampaignCards = (cards: Array<ClientCampaignCard>, cardType: CardTypeEnum) => {
  const type = cardType || cards?.[0]?.state
  switch (type) {
    case CardTypeEnum.ONGOING:
      return sortOngoingCampaignCards(cards)
    case CardTypeEnum.COMING_SOON:
      return sortComingSoonCards(cards)
    case CardTypeEnum.ARCHIVED:
    case CardTypeEnum.FINISHED:
      return sortFinishedCampaignCards(cards)
    case CardTypeEnum.UPCOMING:
      return sortUpcomingCampaignCards(cards)
  }
}

export const arrayMove = (array, from, to) => {
  const startIndex = to < 0 ? array.length + to : to
  const item = array.splice(from, 1)[0]
  array.splice(startIndex, 0, item)
  return array
}
